import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface InfoPopoverProps {
  /** Full text shown in the popover (line breaks preserved via CSS pre-line). */
  text: string;
  /** Accessible label for the info-icon trigger. */
  ariaLabel: string;
}

/**
 * Info icon that toggles a click-opened popover rendered through a portal with
 * `position: fixed` (never clipped by an ancestor's `overflow`, mirrors
 * `HoverTooltip`). Unlike a hover tooltip the popover stays open, so long
 * texts can be scrolled (`.info-popover` caps the height); it closes on an
 * outside click or Escape.
 */
const InfoPopover: React.FC<InfoPopoverProps> = ({ text, ariaLabel }) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLSpanElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  const toggle = useCallback(() => {
    if (coords) {
      setCoords(null);
      return;
    }
    const el = triggerRef.current;
    if (!el) {
      return;
    }
    const rect = el.getBoundingClientRect();
    setCoords({ top: rect.bottom + 8, left: rect.left + rect.width / 2 });
  }, [coords]);

  // Close on outside click / Escape while open.
  useEffect(() => {
    if (!coords) {
      return;
    }
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target) || triggerRef.current?.contains(target)) {
        return;
      }
      setCoords(null);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setCoords(null);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [coords]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="info-popover__trigger"
        aria-label={ariaLabel}
        aria-expanded={coords !== null}
        onClick={toggle}
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
          <path
            d="M8 7.2v4M8 4.6h.01"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </button>
      {coords &&
        createPortal(
          <span
            ref={popoverRef}
            className="info-popover"
            role="tooltip"
            style={{ top: coords.top, left: coords.left }}
          >
            {text}
          </span>,
          document.body
        )}
    </>
  );
};

export default InfoPopover;
